/**
 * RouteErrorBoundary — wraps each route so an unexpected component
 * crash renders a real, dismissible error panel instead of a blank
 * white screen. Especially valuable since prod uses minified React
 * which gives error #300 / #185 codes with no stack frames.
 *
 * If you see this in the wild, the right fix is almost always to
 * harden the offending component's data-shape assumptions (optional
 * chaining, defaulting on undefined fields, etc.) rather than to
 * extend the boundary.
 */
import { Component, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, RotateCw, TriangleAlert } from "lucide-react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string }) {
     
    console.error("RouteErrorBoundary caught:", error, info?.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="cmcp-page-narrow">
          <div role="alert" className="cmcp-card overflow-hidden">
            <div className="flex items-start gap-3 border-b border-stone-100 p-5">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-danger-soft text-danger" aria-hidden="true">
                <TriangleAlert size={17} />
              </span>
              <div>
                <h1 className="text-base font-semibold text-ink">This page crashed</h1>
                <p className="mt-1 text-sm text-stone-600">
                  A component on this route threw an exception while rendering. Reloading the
                  page often clears it.
                </p>
              </div>
            </div>
            <div className="space-y-4 p-5">
              {this.state.error && (
                <pre className="overflow-x-auto rounded-md border border-stone-200 bg-stone-50 p-3 font-mono text-xs text-danger">
                  {this.state.error.message}
                </pre>
              )}
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => location.reload()} className="cmcp-btn-primary">
                  <RotateCw size={15} aria-hidden="true" /> Reload page
                </button>
                <Link to="/dashboard" className="cmcp-btn">
                  <ArrowLeft size={15} aria-hidden="true" /> Back to dashboard
                </Link>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
